import { Loader2, Lock, MinusCircle, PlusCircle, RefreshCcw, Save, Settings } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import ToastMsg from "../../components/common/ToastMsg";
import { PASSWORD_POLICY, BRANCH } from "../../constants/api";
import { useEffect, useState } from "react";
import useApiMethods from "../../lib/useApiMethods";
import { Checkbox } from "antd";
import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { checkUserPermissionFunc } from "../../components/common/CommonFunctions";
import {
    getActiveBranchId,
    getSessionBranchId,
    isOrgAdminUser,
    isOrgOverview,
    BRANCH_FILTER_CHANGED_EVENT,
} from "../../utils/branchSession";
import useBranchRole from "../../hooks/useBranchRole";
import { ConfigBranchSelect } from "../settings/configuration/settingUi";

const PasswordPolicy = () => {
    const { fetch, update } = useApiMethods();
    const { isBranchManager } = useBranchRole();
    const isOrgAdmin = isOrgAdminUser();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [branches, setBranches] = useState([]);
    const [selectedBranchId, setSelectedBranchId] = useState(null);
    const [, setBranchScopeTick] = useState(0);

    const { edit: editPermission } = checkUserPermissionFunc("administrator", "Password Policy", "SubMenu") || {};

    const [formData, setFormData] = useState({
        id: null,
        min_length: null,
        max_length: null,
        min_number: null,
        max_number: null,
        min_lower: null,
        max_lower: null,
        min_upper: null,
        max_upper: null,
        min_chars: null,
        max_chars: null,
        password_combination: true,
        reset_list: 'mail',
        expairy_period: 90
    });

    const loadBranches = async () => {
        if (!isOrgAdmin) {
            const branchId = getSessionBranchId();
            if (isBranchManager && branchId) {
                setSelectedBranchId(String(branchId));
            }
            return branchId;
        }

        const response = await fetch(BRANCH, { drop_down: "True" });
        if (response?.status !== "success") {
            setBranches([]);
            return null;
        }

        const raw = response?.data;
        const list = Array.isArray(raw) ? raw : (raw?.result || raw?.data || []);
        const activeBranches = Array.isArray(list)
            ? list.filter((branch) => branch.is_active !== false)
            : [];
        setBranches(activeBranches);

        const mainBranch = activeBranches.find((branch) => branch.is_main_branch) || activeBranches[0];
        let defaultId = mainBranch ? String(mainBranch.id) : null;

        const headerBranch = getActiveBranchId();
        if (
            headerBranch &&
            headerBranch !== "all" &&
            activeBranches.some((branch) => String(branch.id) === String(headerBranch))
        ) {
            defaultId = String(headerBranch);
        }

        setSelectedBranchId(defaultId);
        return defaultId;
    };

    const fetchPasswordPolicy = async (branchId = selectedBranchId) => {
        try {
            setLoading(true);
            const params = branchId ? { branch: branchId } : {};
            const response = await fetch(PASSWORD_POLICY, params);
            if (response?.status === "success" && response.data?.length > 0) {
                const policyData = response.data[0];
                setFormData(prev => ({
                    ...prev,
                    ...policyData,
                    password_combination: policyData.password_combination,
                    reset_list: policyData.reset_list
                }));
            }
        } catch (error) {
            ToastMsg('error', 'Failed to fetch password policy')
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const init = async () => {
            const branchId = await loadBranches();
            await fetchPasswordPolicy(branchId);
        };
        init();
        const onBranchChange = async () => {
            setBranchScopeTick((n) => n + 1);
            if (!isOrgAdmin) return;
            const headerBranch = getActiveBranchId();
            if (headerBranch && headerBranch !== "all") {
                setSelectedBranchId(String(headerBranch));
                await fetchPasswordPolicy(String(headerBranch));
                return;
            }
            const branchId = await loadBranches();
            await fetchPasswordPolicy(branchId);
        };
        window.addEventListener(BRANCH_FILTER_CHANGED_EVENT, onBranchChange);
        return () => window.removeEventListener(BRANCH_FILTER_CHANGED_EVENT, onBranchChange);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleBranchChange = async (branchId) => {
        setSelectedBranchId(branchId);
        await fetchPasswordPolicy(branchId);
    };

    const handleChange = (field, value) => {
        if (field === 'password_combination') {
            setFormData(prev => ({
                ...prev,
                [field]: Boolean(value)
            }));
            return;
        }

        if (['min_length', 'max_length', 'min_number', 'max_number',
            'min_lower', 'max_lower', 'min_upper', 'max_upper',
            'min_chars', 'max_chars', 'expairy_period'].includes(field)) {

            if (value === '' || (/^\d+$/.test(value) && parseInt(value) >= 0)) {
                const numValue = value === '' ? 0 : parseInt(value);

                if (field === 'min_length' || field === 'max_length') {
                    if (numValue <= 20) {
                        setFormData(prev => ({ ...prev, [field]: numValue }));
                    }
                } else if (field === 'expairy_period') {
                    if (numValue <= 365) {
                        setFormData(prev => ({ ...prev, [field]: numValue }));
                    }
                } else {
                    setFormData(prev => ({ ...prev, [field]: numValue }));
                }
            }
        } else {
            setFormData(prev => ({ ...prev, [field]: value }));
        }
    };

    const handleClear = () => {
        setFormData({
            id: formData.id,
            min_length: null,
            max_length: null,
            min_number: null,
            max_number: null,
            min_lower: null,
            max_lower: null,
            min_upper: null,
            max_upper: null,
            min_chars: null,
            max_chars: null,
            password_combination: true,
            reset_list: 'mail',
            expairy_period: 90
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const minMaxPairs = [
            ["min_length", "max_length", "Length"],
            ["min_number", "max_number", "Numbers"],
            ["min_lower", "max_lower", "Lowercase"],
            ["min_upper", "max_upper", "Uppercase"],
            ["min_chars", "max_chars", "Special Characters"],
        ];
        const requiredNumeric = [
            ["min_length", "Minimum Length"],
            ["max_length", "Maximum Length"],
            ["min_number", "Minimum Numbers"],
            ["max_number", "Maximum Numbers"],
            ["min_lower", "Minimum Lowercase"],
            ["max_lower", "Maximum Lowercase"],
            ["min_upper", "Minimum Uppercase"],
            ["max_upper", "Maximum Uppercase"],
            ["min_chars", "Minimum Special Chars"],
            ["max_chars", "Maximum Special Chars"],
        ];
        for (const [field, label] of requiredNumeric) {
            if (formData[field] === null || formData[field] === undefined || formData[field] === "") {
                ToastMsg("warn", `Please enter ${label}`);
                return;
            }
        }

        for (const [minKey, maxKey, label] of minMaxPairs) {
            const minVal = Number(formData[minKey]);
            const maxVal = Number(formData[maxKey]);
            if (Number.isFinite(minVal) && Number.isFinite(maxVal) && minVal > maxVal) {
                ToastMsg("warn", `Minimum ${label} cannot be greater than Maximum ${label}`);
                return;
            }
        }

        if (branches.length > 0 && !selectedBranchId) {
            ToastMsg('warn', 'Please select a branch to save password policy');
            return;
        }

        try {
            setSaving(true);
            const payload = { ...formData };
            if (selectedBranchId) {
                payload.branch = selectedBranchId;
            }
            const response = await update(PASSWORD_POLICY, payload);

            if (response?.status === "success") {
                ToastMsg('success', 'Password policy updated successfully')
                if (selectedBranchId) {
                    await fetchPasswordPolicy(selectedBranchId);
                }
            } else {
                ToastMsg('error', response.message || "Failed to update password policy")
            }
        } catch (error) {
            ToastMsg('error', 'Failed to update password policy')
        } finally {
            setSaving(false);
        }
    };

    const NumberInput = ({ label, field, max }) => {
        return (
            <div className="space-y-2 flex items-center justify-between w-72">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {label}
                </label>
                <div className="flex items-center gap-2">
                    <Input
                        type="number"
                        value={formData[field] || ''}
                        onChange={(e) => handleChange(field, e.target.value)}
                        min={0}
                        max={max}
                        className="w-20"
                    />
                    <span className="text-sm text-muted-foreground">/ {max}</span>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="h-[calc(100vh-100px)] dark:bg-gray-900 flex items-center justify-center rounded-md">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary"></div>
            </div>
        );
    }

    return (
        <ContainerCard>
            <ModuleTitleCard Icon={Lock} title={'Password Policy'} subtitle="Configure password security requirements per branch" />

            {isOrgAdmin && isOrgOverview() && branches.length > 0 && (
                <div className="flex items-center gap-3 mb-4">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Branch</span>
                    <ConfigBranchSelect
                        branches={branches}
                        value={selectedBranchId}
                        onChange={handleBranchChange}
                    />
                </div>
            )}

            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 shadow-sm">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div className="space-y-6">
                        <div>
                            <h3 className="text-sm font-semibold text-sidebar dark:text-gray-100 mb-3 flex items-center gap-2">
                                <MinusCircle className="w-4 h-4 text-sidebar-primary" />
                                Minimum Requirements
                            </h3>
                            <div className="space-y-4">
                                <NumberInput label="Minimum Length" field="min_length" max={20} />
                                <NumberInput label="Minimum Numbers" field="min_number" max={10} />
                                <NumberInput label="Minimum Lowercase" field="min_lower" max={10} />
                                <NumberInput label="Minimum Uppercase" field="min_upper" max={10} />
                                <NumberInput label="Minimum Special Chars" field="min_chars" max={10} />
                            </div>
                        </div>
                    </div>

                    <div className="space-y-6">
                        <div>
                            <h3 className="text-sm font-semibold text-sidebar dark:text-gray-100 mb-3 flex items-center gap-2">
                                <PlusCircle className="w-4 h-4 text-sidebar-primary" />
                                Maximum Limits
                            </h3>
                            <div className="space-y-4">
                                <NumberInput label="Maximum Length" field="max_length" max={20} />
                                <NumberInput label="Maximum Numbers" field="max_number" max={10} />
                                <NumberInput label="Maximum Lowercase" field="max_lower" max={10} />
                                <NumberInput label="Maximum Uppercase" field="max_upper" max={10} />
                                <NumberInput label="Maximum Special Chars" field="max_chars" max={10} />
                            </div>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <h3 className="text-sm font-semibold text-sidebar dark:text-gray-100 mb-3 flex items-center gap-2">
                            <Settings className="w-4 h-4 text-sidebar-primary" />
                            Additional Settings
                        </h3>

                        <div className="space-y-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                            <div className="flex items-start gap-3">
                                <Checkbox
                                    id="password_combination"
                                    checked={formData.password_combination}
                                    onChange={(e) => handleChange('password_combination', e.target.checked)}
                                    className="mt-0.5"
                                />
                                <div className="flex-1">
                                    <label htmlFor="password_combination" className="text-sm font-medium text-gray-900 dark:text-gray-100 block mb-1">
                                        Require Password Combination
                                    </label>
                                    <p className="text-xs text-muted-foreground">
                                        Password must include a combination of alphabets, numbers, and special characters
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                            <div className="flex items-start gap-3">
                                <Checkbox
                                    id="reset_list"
                                    checked={formData.reset_list}
                                    onChange={(e) => handleChange('reset_list', e.target.checked)}
                                    className="mt-0.5"
                                    disabled
                                />
                                <div className="flex-1">
                                    <label htmlFor="reset_list" className="text-sm font-medium text-gray-900 dark:text-gray-100 block mb-1">
                                        Send Reset OTP via Email
                                    </label>
                                    <p className="text-xs text-muted-foreground">
                                        Forgot password reset OTP will be sent to user's email address
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                            <label className="text-sm font-medium text-gray-900 dark:text-gray-100 block mb-2">
                                Password Expiry Period (Days)
                            </label>
                            <div className="flex items-center gap-3">
                                <Input
                                    type="number"
                                    value={formData.expairy_period || ''}
                                    onChange={(e) => handleChange('expairy_period', e.target.value)}
                                    min={0}
                                    max={365}
                                    className="w-20"
                                />
                                <span className="text-sm text-muted-foreground">days</span>
                            </div>
                            <p className="text-xs text-muted-foreground mt-2">
                                Number of days after which passwords must be changed (0 = never expire)
                            </p>
                        </div>
                    </div>
                </div>

                {
                    editPermission &&
                    <div className="flex justify-end gap-3 pt-6 mt-3 border-t border-gray-200 dark:border-gray-700">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleClear}
                            className="gap-2 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600"
                        >
                            <RefreshCcw className="w-4 h-4" />
                            Clear
                        </Button>
                        <Button
                            size="sm"
                            variant="brand"
                            onClick={handleSubmit}
                            disabled={saving}
                            className="gap-2 transition-colors duration-200"
                        >
                            {saving ?
                                <Loader2 className="w-4 h-4 animate-spin" />
                                :
                                <Save className="w-4 h-4" />
                            }
                            Update Policy
                        </Button>
                    </div>
                }
            </div>
        </ContainerCard>
    );
}

export default PasswordPolicy;
